import { useState, useCallback, useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GROUPED_ASSETS_QUERY } from "../../../../constants/graphql/queries";
import useQueryParams from "../../../../hooks/useQueryParams";
import { GROUP_VIEW_FILTER_PARAMS } from "../groupViewParams";

const LIMIT = 200;

const initialVariables = {
  sortBy: "sku_number",
  sortDirection: "asc",
  page: 1,
  limit: LIMIT,
};

const resolveWarehouseId = searchParams => {
  const value = searchParams.get(GROUP_VIEW_FILTER_PARAMS.warehouseId);
  if (!value || value === "all") return undefined;
  return value;
};

export default function useGroupedAssets(projectId) {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState("");
  const searchParams = useQueryParams();
  const warehouseId = resolveWarehouseId(searchParams);
  const hideDepletedAssets = searchParams.get(GROUP_VIEW_FILTER_PARAMS.inventory) !== "all";

  const queryVariables = useMemo(
    () => ({
      ...initialVariables,
      projectId,
      search,
      hideDepletedAssets,
      warehouseId,
    }),
    [projectId, search, hideDepletedAssets, warehouseId]
  );

  const { data, loading: initialLoading, error, fetchMore, refetch } = useQuery(
    GROUPED_ASSETS_QUERY,
    {
      variables: queryVariables,
      notifyOnNetworkStatusChange: true,
    }
  );

  const collection = data?.groupedAssets?.collection || [];
  const metadata = data?.groupedAssets?.metadata || {};
  const hasNextPage = metadata.currentPage < metadata.totalPages;

  const handleSearch = useCallback(
    async term => {
      setSearchLoading(true);
      setSearch(term);
      try {
        await refetch({
          ...initialVariables,
          projectId,
          search: term,
          hideDepletedAssets,
          warehouseId,
        });
      } catch (error) {
        console.error("Error performing search:", error);
      } finally {
        setSearchLoading(false);
      }
    },
    [refetch, projectId, hideDepletedAssets, warehouseId]
  );

  const loadMore = useCallback(async () => {
    if (loading || !hasNextPage || !metadata.currentPage) return;

    setLoading(true);

    try {
      await fetchMore({
        variables: {
          ...initialVariables,
          projectId,
          page: metadata.currentPage + 1,
          search: search,
          hideDepletedAssets,
          warehouseId,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;

          const existingAssets = new Set(
            previousResult.groupedAssets.collection.map(item => item.skuNumber)
          );
          const newAssets = fetchMoreResult.groupedAssets.collection.filter(
            item => !existingAssets.has(item.skuNumber)
          );

          if (newAssets.length === 0) return previousResult;

          return {
            ...previousResult,
            groupedAssets: {
              ...previousResult.groupedAssets,
              collection: [...previousResult.groupedAssets.collection, ...newAssets],
              metadata: fetchMoreResult.groupedAssets.metadata,
            },
          };
        },
      });
    } catch (error) {
      console.error("Error loading more assets:", error);
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasNextPage,
    metadata.currentPage,
    fetchMore,
    projectId,
    search,
    hideDepletedAssets,
    warehouseId,
  ]);

  const handleScroll = useCallback(
    e => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const scrolledToBottom = scrollHeight - scrollTop <= clientHeight + 100; // 100px threshold

      if (scrolledToBottom && hasNextPage && !loading) {
        loadMore();
      }
    },
    [hasNextPage, loading, loadMore]
  );

  return {
    collection,
    initialLoading,
    loading,
    searchLoading,
    error,
    hasNextPage,
    search,
    handleSearch,
    handleScroll,
  };
}
